import { useState } from "react";
import * as Sentry from "@sentry/react";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { useActiveUploads } from "lib/hooks/api/customer_documents/useActiveUploads";
import { useDisconnectIntegration } from "lib/hooks/api/integrations/useDisconnectIntegration";
import { paragon } from "lib/integrations/paragon";

export default function DisconnectButton({ integration }: { integration: string }) {
  const [isOpen, setIsOpen] = useState(false);

  const closeModal = () => setIsOpen(false);

  const { mutateAsync: disconnectIntegration } = useDisconnectIntegration();
  const { groups, isLoading: isLoadingUploads } = useActiveUploads({ enabled: isOpen });

  const activeGroup = groups.find(
    (g) => g.source === integration && g.processedCount < g.totalCount,
  );
  const hasSyncInProgress = !isLoadingUploads && !!activeGroup;

  const handleDisconnect = async () => {
    try {
      await disconnectIntegration({ req: { integration } });
      await paragon.uninstallIntegration(integration);
      toast.success(
        `Integration disconnected successfully. Files associated with the integration will be deleted.`,
      );
      closeModal();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to disconnect integration. Please try again.");
    }
  };

  return (
    <>
      <Button buttonStyle="destructive" onClick={() => setIsOpen(true)}>
        Disconnect
      </Button>
      <Modal
        open={isOpen}
        showCloseButton={false}
        onOpenChange={closeModal}
        padding={32}
        maxWidth="440px"
      >
        <Flex flexDirection="column" gap={16} aria-label="Disconnect integration modal">
          <Flex flexDirection="column" gap={8}>
            <Text bold>About to disconnect integration</Text>
            {hasSyncInProgress ? (
              <>
                <Text>
                  Files are still being synced ({activeGroup.processedCount} of{" "}
                  {activeGroup.totalCount} processed). Disconnecting now will cancel any remaining
                  syncs and those files will not be imported.
                </Text>
                <Text>
                  Are you sure you want to disconnect? This will also delete all files already
                  synced with the integration.
                </Text>
              </>
            ) : (
              <Text>
                Are you sure you want to disconnect this integration? This will delete all files
                synced with the integration.
              </Text>
            )}
          </Flex>
          <Flex gap={8} alignSelf="flex-end">
            <Button buttonType="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              buttonStyle="destructive"
              onClick={handleDisconnect}
              disabled={isLoadingUploads}
            >
              {hasSyncInProgress ? "Disconnect anyway" : "Disconnect"}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
